import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export function CreateDrawer({ open, onOpenChange, title, fields, onSubmit, submitting, initial, mode = "create" }) {
  const { brands } = useApp();
  const [form, setForm] = useState(initial || {});

  useEffect(() => {
    if (open) setForm(initial || {});
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const missing = fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    try {
      await onSubmit(form);
      toast.success(mode === "edit" ? `${title} updated` : `${title} created`);
      setForm({});
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="create-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">{mode === "edit" ? `Edit ${title}` : `New ${title}`}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{f.label}{f.required && <span className="text-brand-pink"> *</span>}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} className="rounded-xl" />
              ) : f.type === "select" ? (
                <select data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  className="flex h-10 w-full rounded-xl border border-input bg-surface-raised px-3 text-sm">
                  <option value="">Select…</option>
                  {(f.name === "brand" ? brands.map((b) => b.name) : f.options).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input data-testid={`field-${f.name}`} type={f.type || "text"} value={form[f.name] ?? ""} onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)} placeholder={f.placeholder} className="rounded-xl" />
              )}
            </div>
          ))}
          <Button onClick={submit} disabled={submitting} data-testid="submit-create" className="w-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white">
            {submitting ? "Saving…" : mode === "edit" ? "Save changes" : `Create ${title}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
